/**
 * The comment thread of a venue card — contract #17 (read, VIEWER+) and #16
 * (write, MANAGER+), task T.C4a.
 *
 * The AC is "comments post appears without reload": `useCreateVenueComment`
 * appends the optimistic row to the query cache, so the composer's text is on
 * screen before the api has answered, and the refetch afterwards swaps it for
 * the server's row (real id, resolved author) without any user-visible flash.
 */

import { useState } from 'react';

import type { ApiError } from '../../api/client';
import type { CommentDto } from '../../api/comments';
import { useCreateVenueComment, useVenueComments } from '../../api/comments';
import { Button, EmptyState, ErrorState, Spinner, Textarea } from '../../components/ui';

import { describeFailure } from './venues-model';
import { formatDateTime } from '../dashboard/format';

/** Rows this browser drew before the api answered carry a negative id. */
function isOptimistic(comment: CommentDto): boolean {
  return comment.id < 0;
}

export function VenueComments({
  venueId,
  commentCount,
  canPost,
}: {
  venueId: number;
  /** `commentCount` of #7 — the thread's own size, as the detail fan-out reported it. */
  commentCount: number;
  canPost: boolean;
}) {
  const query = useVenueComments(venueId);
  const create = useCreateVenueComment(venueId);
  const [draft, setDraft] = useState('');

  const comments = query.data ?? [];
  // The api owns the truth; the count is only the fallback for the moment
  // before #17 answers (and the hint when the thread endpoint is unavailable).
  const total = comments.length > 0 ? comments.length : commentCount;

  const submit = () => {
    const body = draft.trim();
    if (!body) return;
    create.mutate(body, { onSuccess: () => setDraft('') });
  };

  return (
    <section className="card venue-section" data-testid="venue-comments">
      <header className="venue-section__head">
        <h2 className="venue-section__title">Комментарии</h2>
        <span className="venue-section__hint" data-testid="comments-count">
          {total}
        </span>
      </header>

      <div className="venue-section__body">
        {query.isPending ? (
          <Spinner label="Загружаем комментарии" />
        ) : query.isError ? (
          <ErrorState
            message={describeFailure(query.error, 'Не удалось загрузить комментарии.')}
            onRetry={() => void query.refetch()}
          />
        ) : comments.length === 0 ? (
          <EmptyState title="Комментариев пока нет" hint="Первый комментарий оставит историю переписки по площадке." />
        ) : (
          <div className="comments" data-testid="comments-list">
            {comments.map((comment) => (
              <CommentRow key={comment.id} comment={comment} />
            ))}
          </div>
        )}

        {canPost ? (
          <form
            className="comment-form"
            data-testid="comment-form"
            onSubmit={(event) => {
              event.preventDefault();
              submit();
            }}
          >
            <Textarea
              aria-label="Новый комментарий"
              data-testid="comment-input"
              rows={3}
              placeholder="Комментарий по площадке…"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                // Ctrl/Cmd+Enter posts, the way a chat does; a bare Enter is a
                // newline because venue notes are more than one line long.
                if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
                  event.preventDefault();
                  submit();
                }
              }}
            />
            {create.isError ? (
              <p className="field__error" role="alert" data-testid="comment-error">
                {describeFailure(create.error as ApiError | null, 'Не удалось отправить комментарий.')}
              </p>
            ) : null}
            <div className="comment-form__row">
              <span className="venue-section__hint">Ctrl/⌘ + Enter — отправить</span>
              <Button
                variant="primary"
                size="sm"
                loading={create.isPending}
                disabled={!draft.trim()}
                data-testid="comment-submit"
                onClick={submit}
              >
                Отправить
              </Button>
            </div>
          </form>
        ) : (
          <p className="venue-section__hint" data-testid="comments-read-only">
            Комментарии могут оставлять менеджеры и администраторы.
          </p>
        )}
      </div>
    </section>
  );
}

/**
 * The label above a comment's body. The optimistic row is by construction THIS
 * session's draft, so it reads «Вы»; a persisted row renders the name the api
 * resolved — and when the author has no `displayName` (a Yandex profile without
 * one, or a seed row the owner has not aliased yet) it degrades to a neutral
 * «Пользователь», never to a literal null and never to a false «Вы» on somebody
 * else's comment (T.Z66 — the fallback used to be unconditional).
 */
function authorLabel(comment: CommentDto, optimistic: boolean): string {
  if (optimistic) return 'Вы';
  return comment.authorName && comment.authorName.trim() !== '' ? comment.authorName : 'Пользователь';
}

function CommentRow({ comment }: { comment: CommentDto }) {
  const optimistic = isOptimistic(comment);
  return (
    <article
      className={optimistic ? 'comment comment--optimistic' : 'comment'}
      data-testid="comment"
      data-comment-id={comment.id}
      data-optimistic={optimistic ? 'true' : undefined}
    >
      <p className="comment__body" data-testid="comment-body">
        {comment.body}
      </p>
      <p className="comment__meta">
        <span className="comment__author" data-testid="comment-author">
          {authorLabel(comment, optimistic)}
        </span>
        <span data-testid="comment-date">{formatDateTime(comment.createdAt)}</span>
      </p>
    </article>
  );
}
